import { gql, useMutation } from '@apollo/client'
import { useNotification } from '@btime/react-messager'
import { useTranslation } from 'react-i18next'
import React from 'react'

import { serviceOrdersClient } from '../../utils/apollo/api'
import { ACTIVITY_TAG_TYPE } from '../../modules/tags/_constants'
import { useServiceOrderPermissions } from '../../utils/permissions/serviceOrders/serviceOrderPermissions'
import Link from '../../components/actions/Link'
import Loading from '../../packages/kiti/components/helpers/Loading'
import TagPresenter from '../tags/TagPresenter'
import TagSelect from '../tags/select/TagSelect'
import Text from '../../packages/kiti/components/presentation/Text'

const MUTATION = gql`
  mutation UpdateServiceOrderTag($id: Int, $input: ServiceOrderInput) {
    updateServiceOrder(id: $id, input: $input) {
      id
      tagId
      tag {
        id
        name
        color
      }
    }
  }
`

const TAG_FRAGMENT = gql`
  fragment ServiceOrderTagFields on ServiceOrder {
    tagId
    tag {
      id
      name
      color
    }
  }
`

export const useUpdateServiceOrderTag = () => {
  const notify = useNotification()
  // Sem `refetchQueries` de propósito: recarregar `ServiceOrders` refaz a query paginada com a
  // página 1 e faz a atividade editada "sumir" quando ela está além da primeira página. O `ServiceOrder`
  // é normalizado por id no cache do Apollo, então a resposta da mutation (que o back devolve com
  // `tag { id name color }` preenchido) + o write de fallback em `update` já atualizam a linha, o card
  // do Kanban e o detalhe aberto. `ServiceOrdersCount` não muda ao setar tag. Se houver filtro ativo
  // por tag, a lista pode ficar defasada até o próximo polling — preferível a resetar a paginação.
  const [request, state] = useMutation(MUTATION, { client: serviceOrdersClient })

  const handleRequest = (serviceOrder, tag) => {
    return request({
      variables: { id: serviceOrder.id, input: { tagId: tag?.id || null } },
      // Após a confirmação do back, grava no cache a tag escolhida (ou a ausência dela) — assim a linha
      // atualiza na hora mesmo que a resposta da mutation venha sem o objeto `tag` preenchido.
      update: cache => {
        const id = cache.identify({ __typename: serviceOrder.__typename || 'ServiceOrder', id: serviceOrder.id })
        if (!id) return
        cache.writeFragment({
          id,
          fragment: TAG_FRAGMENT,
          data: {
            __typename: serviceOrder.__typename || 'ServiceOrder',
            tagId: tag?.id || null,
            tag: tag ? { __typename: tag.__typename || 'Tag', id: tag.id, name: tag.name, color: tag.color } : null
          }
        })
      }
    })
      .then(resp => {
        if (!!resp?.errors) notify.error({ title: 'Ocorreu um erro ao salvar a tag!' })
      })
      .catch(() => notify.error({ title: 'Ocorreu um erro ao salvar a tag!' }))
  }

  return [handleRequest, state]
}

/**
 * Edição da tag direto na linha da listagem (e no card do Kanban), sem precisar abrir os detalhes
 * da atividade — requisito específico da tag, que a Prioridade não tem hoje. Segue o mesmo padrão
 * usado em `EditableSentence` (automações): alterna entre um label clicável e o próprio Select
 * aberto no lugar, disparando a mutação diretamente ao selecionar (sem modal, sem submit).
 */
const EditableServiceOrdersTag = ({ serviceOrder }) => {
  const { t } = useTranslation('serviceOrders')
  const [isEditing, setIsEditing] = React.useState(false)
  const [updateTag, { loading }] = useUpdateServiceOrderTag()
  const { canEdit: canEditAll } = useServiceOrderPermissions(serviceOrder)

  // Enquanto a tag é salva (e a lista atualiza), mostra os "3 pontinhos" de loading em vez de deixar
  // a célula em branco até a tag aparecer.
  if (loading) return <Loading size="small" justify="flex-start" />

  if (isEditing) {
    return (
      <TagSelect
        size="small"
        autoFocus
        type={ACTIVITY_TAG_TYPE}
        allowCreate={false}
        value={serviceOrder?.tag}
        onClose={() => setIsEditing(false)}
        onChange={tag => updateTag(serviceOrder, tag)}
      />
    )
  }

  if (!serviceOrder?.tag) {
    return canEditAll ? (
      <Link onClick={() => setIsEditing(true)}>
        <Text color="purple">{t('selectTag')}</Text>
      </Link>
    ) : (
      false
    )
  }

  return (
    <Link onClick={() => canEditAll && setIsEditing(true)} block>
      <TagPresenter tag={serviceOrder.tag} />
    </Link>
  )
}

export default EditableServiceOrdersTag
